import NavBar from '../components/NavBar';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

export default function IngestServers() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [servers, setServers] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name:'', host:'', rtmp_port:1935, srt_port:10080, region:'' });
  const [checking, setChecking] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/ingest-servers'); setServers(data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkAll = async () => {
    setChecking('all');
    try { await api.post('/ingest-servers/check-all'); load(); }
    catch {} finally { setChecking(null); }
  };

  const checkOne = async (id) => {
    setChecking(id);
    try { await api.post(`/ingest-servers/${id}/check`); load(); }
    catch {} finally { setChecking(null); }
  };

  const handleSave = async () => {
    if (!form.name || !form.host) { setError('Name and host required'); return; }
    setError(''); setLoading(true);
    try {
      if (modal === 'create') {
        await api.post('/ingest-servers', form);
      } else {
        await api.patch(`/ingest-servers/${modal.id}`, form);
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setLoading(false); }
  };

  const deleteServer = async (s) => {
    if (s.is_primary) return alert('Cannot delete primary server');
    if (!window.confirm(`Remove server "${s.name}"?`)) return;
    await api.delete(`/ingest-servers/${s.id}`);
    load();
  };

  const toggleEnabled = async (s) => {
    await api.patch(`/ingest-servers/${s.id}`, { enabled: !s.enabled });
    load();
  };

  const statusColor = (st) => ({
    online: 'var(--signal)', offline: 'var(--danger)', unknown: 'var(--text-muted)'
  }[st] || 'var(--text-muted)');

  const s = {
    page: { minHeight:'100vh', background:'var(--bg)' },
    nav: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 32px', height:'58px', borderBottom:'1px solid var(--border)', background:'var(--surface)', position:'sticky', top:0, zIndex:10 },
    navLogo: { display:'flex', alignItems:'center', gap:'9px', fontWeight:'800', fontSize:'17px' },
    navDot: { width:'9px', height:'9px', borderRadius:'50%', background:'var(--signal)', boxShadow:'0 0 8px var(--signal)' },
    navRight: { display:'flex', alignItems:'center', gap:'10px' },
    navBtn: (h) => ({ fontSize:'13px', padding:'6px 14px', border:`1px solid ${h?'rgba(0,230,118,0.3)':'var(--border)'}`, borderRadius:'7px', background:'transparent', color:h?'var(--signal)':'var(--text-muted)', cursor:'pointer' }),
    main: { maxWidth:'900px', margin:'0 auto', padding:'36px 24px' },
    header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'28px', gap:'12px', flexWrap:'wrap' },
    title: { fontSize:'24px', fontWeight:'800', letterSpacing:'-0.5px' },
    headerBtns: { display:'flex', gap:'8px' },
    addBtn: { background:'var(--signal)', color:'#000', fontWeight:'700', fontSize:'13px', borderRadius:'8px', padding:'9px 18px', cursor:'pointer', border:'none' },
    checkBtn: { background:'var(--surface2)', border:'1px solid var(--border)', color:'var(--text)', fontWeight:'600', fontSize:'13px', borderRadius:'8px', padding:'9px 18px', cursor:'pointer' },
    serverCard: (enabled) => ({ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'12px', padding:'18px 20px', marginBottom:'12px', opacity:enabled?1:0.6 }),
    serverHeader: { display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px' },
    statusDot: (st) => ({ width:'9px', height:'9px', borderRadius:'50%', background:statusColor(st), boxShadow:st==='online'?`0 0 6px ${statusColor(st)}`:'none', flexShrink:0 }),
    serverName: { fontWeight:'700', fontSize:'16px', flex:1 },
    primaryBadge: { fontSize:'10px', fontWeight:'800', padding:'3px 8px', borderRadius:'4px', background:'rgba(0,230,118,0.1)', color:'var(--signal)', border:'1px solid rgba(0,230,118,0.2)' },
    regionBadge: { fontSize:'10px', fontWeight:'700', padding:'3px 8px', borderRadius:'4px', background:'var(--surface2)', color:'var(--text-muted)', border:'1px solid var(--border)' },
    urlGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' },
    urlCard: { background:'var(--surface2)', borderRadius:'8px', padding:'10px 12px', border:'1px solid var(--border)' },
    urlLabel: { fontSize:'10px', fontWeight:'700', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'4px' },
    urlValue: { fontFamily:'var(--mono)', fontSize:'11px', color:'var(--signal)', wordBreak:'break-all' },
    serverActions: { display:'flex', gap:'6px', flexWrap:'wrap' },
    actionBtn: (color) => ({ padding:'5px 12px', borderRadius:'7px', fontSize:'12px', fontWeight:'600', cursor:'pointer', background:color?`${color}15`:'var(--surface2)', color:color||'var(--text-muted)', border:`1px solid ${color?`${color}30`:'var(--border)'}` }),
    // Modal
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:'24px' },
    modalBox: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'32px', width:'100%', maxWidth:'460px' },
    modalTitle: { fontSize:'20px', fontWeight:'800', marginBottom:'24px' },
    field: { marginBottom:'14px' },
    label: { display:'block', fontSize:'11px', fontWeight:'700', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'6px' },
    grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' },
    saveBtn: { width:'100%', padding:'12px', background:'var(--signal)', color:'#000', fontWeight:'700', fontSize:'14px', borderRadius:'var(--radius)', border:'none', cursor:'pointer', marginTop:'8px' },
    cancelBtn: { width:'100%', padding:'11px', background:'var(--surface2)', color:'var(--text)', fontWeight:'600', fontSize:'14px', borderRadius:'var(--radius)', border:'1px solid var(--border)', cursor:'pointer', marginTop:'8px' },
    errorBox: { background:'var(--danger-bg)', border:'1px solid rgba(255,77,77,0.25)', color:'var(--danger)', borderRadius:'8px', padding:'10px 14px', fontSize:'13px', marginBottom:'14px' },
    info: { background:'rgba(0,230,118,0.05)', border:'1px solid rgba(0,230,118,0.15)', borderRadius:'10px', padding:'14px 18px', marginBottom:'24px', fontSize:'13px', color:'var(--text-muted)', lineHeight:'1.6' },
  };

  const isAdmin = user?.role === 'admin';

  return (
    <NavBar>
      <div style={s.page}>
      <nav style={s.nav}>
        <div style={s.navLogo}><div style={s.navDot}/>MultiStream</div>
        <div style={s.navRight}>
          <button style={s.navBtn(false)} onClick={()=>navigate('/dashboard')}>Dashboard</button>
          <button style={s.navBtn(false)} onClick={()=>navigate('/settings')}>Settings</button>
          {isAdmin&&<button style={s.navBtn(true)} onClick={()=>navigate('/admin')}>Users</button>}
          <button style={s.navBtn(false)} onClick={logout}>Sign out</button>
        </div>
      </nav>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Ingest Servers</div>
            <div style={{fontSize:'14px',color:'var(--text-muted)',marginTop:'4px'}}>Multi-server RTMP/SRT ingest configuration</div>
          </div>
          {isAdmin && (
            <div style={s.headerBtns}>
              <button style={s.checkBtn} onClick={checkAll} disabled={checking==='all'}>
                {checking==='all'?'Checking…':'Check All'}
              </button>
              <button style={s.addBtn} onClick={()=>{ setForm({name:'',host:'',rtmp_port:1935,srt_port:10080,region:''}); setError(''); setModal('create'); }}>
                + Add Server
              </button>
            </div>
          )}
        </div>

        <div style={s.info}>
          💡 Add your backup VPS as a second ingest server. DJs can push to either server using the same stream key — both will fan-out to all destinations.
        </div>

        {servers.map(server => (
          <div key={server.id} style={s.serverCard(server.enabled)}>
            <div style={s.serverHeader}>
              <div style={s.statusDot(server.status)}/>
              <div style={s.serverName}>{server.name}</div>
              {server.is_primary && <span style={s.primaryBadge}>PRIMARY</span>}
              {server.region && <span style={s.regionBadge}>{server.region}</span>}
              <span style={{fontSize:'12px',color:statusColor(server.status),fontWeight:'600'}}>{server.status}</span>
            </div>

            <div style={s.urlGrid}>
              <div style={s.urlCard}>
                <div style={s.urlLabel}>RTMP</div>
                <div style={s.urlValue}>{server.rtmpUrl}</div>
              </div>
              <div style={s.urlCard}>
                <div style={s.urlLabel}>RTMPS</div>
                <div style={s.urlValue}>{server.rtmpsUrl}</div>
              </div>
              <div style={{...s.urlCard, gridColumn:'1/-1'}}>
                <div style={s.urlLabel}>SRT</div>
                <div style={s.urlValue}>{server.srtUrl}</div>
              </div>
            </div>

            {server.last_checked_at && (
              <div style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'10px'}}>
                Last checked: {new Date(server.last_checked_at).toLocaleString()}
              </div>
            )}

            {isAdmin && (
              <div style={s.serverActions}>
                <button style={s.actionBtn('#00e676')} onClick={()=>checkOne(server.id)} disabled={checking===server.id}>
                  {checking===server.id?'Checking…':'Health Check'}
                </button>
                <button style={s.actionBtn()} onClick={()=>{ setForm({name:server.name,host:server.host,rtmp_port:server.rtmp_port,srt_port:server.srt_port,region:server.region||''}); setError(''); setModal(server); }}>
                  Edit
                </button>
                <button style={s.actionBtn(server.enabled?null:'#00e676')} onClick={()=>toggleEnabled(server)}>
                  {server.enabled?'Disable':'Enable'}
                </button>
                {!server.is_primary && (
                  <button style={s.actionBtn('#ff4d4d')} onClick={()=>deleteServer(server)}>Remove</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>{modal==='create'?'Add Ingest Server':'Edit Server'}</div>
            {error&&<div style={s.errorBox}>{error}</div>}
            <div style={s.field}>
              <label style={s.label}>Server Name</label>
              <input placeholder="e.g. Backup VPS (Miami)" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
            </div>
            <div style={s.field}>
              <label style={s.label}>Hostname / IP</label>
              <input placeholder="e.g. backup.denneryfm.com or 192.x.x.x" value={form.host} onChange={e=>setForm({...form,host:e.target.value})}/>
            </div>
            <div style={s.grid2}>
              <div style={s.field}>
                <label style={s.label}>RTMP Port</label>
                <input type="number" value={form.rtmp_port} onChange={e=>setForm({...form,rtmp_port:parseInt(e.target.value)})}/>
              </div>
              <div style={s.field}>
                <label style={s.label}>SRT Port</label>
                <input type="number" value={form.srt_port} onChange={e=>setForm({...form,srt_port:parseInt(e.target.value)})}/>
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Region / Label (optional)</label>
              <input placeholder="e.g. Saint Lucia, Backup" value={form.region} onChange={e=>setForm({...form,region:e.target.value})}/>
            </div>
            <button style={s.saveBtn} onClick={handleSave} disabled={loading}>
              {loading?'Saving…':modal==='create'?'Add Server':'Save Changes'}
            </button>
            <button style={s.cancelBtn} onClick={()=>setModal(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
    </NavBar>
  );
}
